import { useState, useEffect, useCallback, useRef } from "react";
import {
  Globe,
  Image as ImageIcon,
  Instagram,
  Facebook,
  Music2,
  Loader2,
  Save,
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Copy,
  Check,
  ExternalLink,
  QrCode,
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  FileText,
  Phone,
  CalendarClock,
  Send,
  AlertCircle,
} from "lucide-react";
import QRCode from "qrcode";
import apiClient from "@/lib/api-client";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PublicProfileImage {
  id: string;
  url: string;
  alt: string | null;
  sortOrder: number;
}

type RequirementKey = "description" | "photos" | "phone" | "schedule";

interface ProfileRequirement {
  key: RequirementKey;
  label: string;
  met: boolean;
  hint: string;
}

type ProfileStatus = "draft" | "pending_review" | "published" | "rejected";

interface PublicProfileData {
  status: ProfileStatus;
  description: string;
  instagramUrl: string | null;
  facebookUrl: string | null;
  tiktokUrl: string | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  images: PublicProfileImage[];
  maxImages: number;
  publicUrl: string;
  isLive: boolean;
  requirements: ProfileRequirement[];
  ready: boolean;
}

interface SubmitErrorPayload {
  data?: {
    requirements?: ProfileRequirement[];
    missing?: string[];
  };
  message?: string;
}

const REQUIREMENT_ICONS: Record<RequirementKey, typeof FileText> = {
  description: FileText,
  photos: ImageIcon,
  phone: Phone,
  schedule: CalendarClock,
};

const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || fallback;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PublicProfile() {
  const { user } = useAuth();
  const { currentLocation, isLoading: locationsLoading } = useLocation();
  const { confirm } = useConfirm();
  const { showToast } = useGlobalToast();

  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadingQr, setDownloadingQr] = useState(false);

  const [description, setDescription] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const locationId = currentLocation?.id ?? null;

  const loadProfile = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/public-profiles/${locationId}`);
      const data: PublicProfileData = res.data.data;
      setProfile(data);
      setDescription(data.description || "");
      setInstagramUrl(data.instagramUrl || "");
      setFacebookUrl(data.facebookUrl || "");
      setTiktokUrl(data.tiktokUrl || "");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al cargar tu página web"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const isDirty =
    !!profile &&
    (description !== (profile.description || "") ||
      instagramUrl !== (profile.instagramUrl || "") ||
      facebookUrl !== (profile.facebookUrl || "") ||
      tiktokUrl !== (profile.tiktokUrl || ""));

  const handleSave = async () => {
    if (!locationId) return;
    setSaving(true);
    try {
      await apiClient.put(`/public-profiles/${locationId}`, {
        description,
        instagramUrl: instagramUrl.trim() || null,
        facebookUrl: facebookUrl.trim() || null,
        tiktokUrl: tiktokUrl.trim() || null,
      });
      await loadProfile();
      showToast("Cambios guardados", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al guardar los cambios"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file || !locationId || !profile) return;

    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      showToast("Formato no válido. Usa JPG, PNG o WebP.", "error");
      return;
    }
    if (profile.images.length >= profile.maxImages) {
      showToast(`Ya tienes el máximo de ${profile.maxImages} fotos.`, "error");
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        await apiClient.post(`/public-profiles/${locationId}/images`, {
          file: base64,
        });
        await loadProfile();
        showToast("Foto añadida", "success");
      } catch (err) {
        showToast(getErrorMessage(err, "Error al subir la foto"), "error");
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      showToast("Error al leer el archivo", "error");
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!locationId) return;
    const ok = await confirm({
      title: "¿Eliminar esta foto?",
      message: "Se quitará de la galería de tu página web.",
      confirmText: "Sí, eliminar",
      danger: true,
    });
    if (!ok) return;

    setDeletingId(imageId);
    try {
      await apiClient.delete(`/public-profiles/${locationId}/images/${imageId}`);
      await loadProfile();
      showToast("Foto eliminada", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al eliminar la foto"), "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleMoveImage = async (imageId: string, direction: "up" | "down") => {
    if (!profile || !locationId) return;
    const sorted = [...profile.images].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((img) => img.id === imageId);
    if (idx === -1) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;

    const reordered = [...sorted];
    [reordered[idx], reordered[targetIdx]] = [reordered[targetIdx], reordered[idx]];
    const imageIds = reordered.map((img) => img.id);

    setReorderingId(imageId);
    // Optimista: reordenamos ya mismo en memoria para que el clic se sienta instantáneo
    setProfile({
      ...profile,
      images: reordered.map((img, i) => ({ ...img, sortOrder: i })),
    });

    try {
      await apiClient.put(`/public-profiles/${locationId}/images/order`, {
        imageIds,
      });
    } catch (err) {
      showToast(getErrorMessage(err, "Error al reordenar las fotos"), "error");
      await loadProfile(); // revertir al orden real del servidor
    } finally {
      setReorderingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!locationId || !profile) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/public-profiles/${locationId}/submit`);
      await loadProfile();
      showToast("Solicitud enviada. La revisaremos en breve.", "success");
    } catch (err) {
      const e = err as { response?: { status?: number; data?: SubmitErrorPayload } };
      if (e.response?.status === 422 && e.response.data?.data?.requirements) {
        const requirements = e.response.data.data.requirements;
        setProfile((prev) => (prev ? { ...prev, requirements, ready: false } : prev));
        showToast("Todavía faltan requisitos por cumplir.", "error");
      } else {
        showToast(getErrorMessage(err, "Error al enviar la solicitud"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!profile?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(profile.publicUrl);
      setCopied(true);
      showToast("Enlace copiado", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("No se pudo copiar el enlace", "error");
    }
  };

  const handleDownloadQr = async () => {
    if (!profile?.publicUrl) return;
    setDownloadingQr(true);
    try {
      const dataUrl = await QRCode.toDataURL(profile.publicUrl, {
        width: 512,
        margin: 2,
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `qr-${currentLocation?.slug || "mi-pagina"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      showToast("Error al generar el código QR", "error");
    } finally {
      setDownloadingQr(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  if (user?.role === "staff") {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Acceso restringido
            </h2>
            <p className="text-gray-500">
              Solo los hosteleros (administradores) pueden gestionar la página
              web pública del local.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (locationsLoading || (loading && !profile)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!currentLocation || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-2xl mx-auto text-center text-gray-500 py-12">
          Selecciona un local para gestionar su página web.
        </div>
      </div>
    );
  }

  const metCount = profile.requirements.filter((r) => r.met).length;
  const totalCount = profile.requirements.length;
  const progressPct = totalCount > 0 ? (metCount / totalCount) * 100 : 0;

  const sortedImages = [...profile.images].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary-600" />
            Mi página web
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {currentLocation.name} — crea una mini-página pública con tus
            fotos, descripción y redes sociales.
          </p>
        </div>

        {/* Status banner */}
        <StatusBanner profile={profile} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-4 md:mt-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* Descripción */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
                Descripción
              </h2>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                maxLength={2000}
                placeholder="Cuéntale a tus clientes qué hace especial a tu local: ambiente, especialidades, historia..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-gray-900 text-sm"
              />
              <div className="flex items-center justify-between mt-1">
                <p
                  className={`text-xs ${
                    profile.requirements.find((r) => r.key === "description")?.met
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                >
                  {description.length} caracteres
                </p>
              </div>
            </div>

            {/* Galería */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base md:text-lg font-semibold text-gray-900">
                  Fotos
                </h2>
                <span className="text-xs text-gray-500">
                  {profile.images.length}/{profile.maxImages}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {sortedImages.map((img, idx) => (
                  <div
                    key={img.id}
                    className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                  >
                    <img
                      src={img.url}
                      alt={img.alt || `Foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleMoveImage(img.id, "up")}
                          disabled={idx === 0 || reorderingId !== null}
                          title="Mover a la izquierda"
                          className="p-1.5 bg-white/90 rounded-md text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveImage(img.id, "down")}
                          disabled={idx === sortedImages.length - 1 || reorderingId !== null}
                          title="Mover a la derecha"
                          className="p-1.5 bg-white/90 rounded-md text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(img.id)}
                        disabled={deletingId === img.id}
                        className="p-1.5 bg-red-600/90 rounded-md text-white hover:bg-red-700 disabled:opacity-50"
                        title="Eliminar foto"
                      >
                        {deletingId === img.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                    <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                      {idx + 1}
                    </span>
                  </div>
                ))}

                {profile.images.length < profile.maxImages && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1.5 text-gray-400 hover:border-primary-400 hover:text-primary-500 transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5" />
                    )}
                    <span className="text-xs font-medium">Subir foto</span>
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleFileSelected}
                className="hidden"
              />

              {profile.images.length === 0 && (
                <p className="text-xs text-gray-400">
                  Necesitas al menos una foto para poder publicar tu página.
                </p>
              )}
            </div>

            {/* Redes sociales */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
                Redes sociales
              </h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="url"
                    value={instagramUrl}
                    onChange={(e) => setInstagramUrl(e.target.value)}
                    placeholder="https://instagram.com/tu-negocio"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="url"
                    value={facebookUrl}
                    onChange={(e) => setFacebookUrl(e.target.value)}
                    placeholder="https://facebook.com/tu-negocio"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Music2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="url"
                    value={tiktokUrl}
                    onChange={(e) => setTiktokUrl(e.target.value)}
                    placeholder="https://tiktok.com/@tu-negocio"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Guardar */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar cambios
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 md:space-y-6">
            {/* Checklist */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Requisitos
                </h2>
                <span className="text-xs font-medium text-gray-500">
                  {metCount} de {totalCount}
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                <div
                  className={`h-full rounded-full transition-all ${
                    metCount === totalCount ? "bg-green-500" : "bg-primary-500"
                  }`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <ul className="space-y-3 mb-4">
                {profile.requirements.map((req) => {
                  const Icon = REQUIREMENT_ICONS[req.key] || FileText;
                  return (
                    <li key={req.key} className="flex items-start gap-2.5">
                      {req.met ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <p
                          className={`text-sm font-medium flex items-center gap-1.5 ${
                            req.met ? "text-gray-900" : "text-gray-700"
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5 text-gray-400" />
                          {req.label}
                        </p>
                        {!req.met && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {req.hint}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {(profile.status === "draft" || profile.status === "rejected") && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !profile.ready}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {profile.status === "rejected"
                    ? "Volver a solicitar publicación"
                    : "Publicar página"}
                </button>
              )}

              {profile.status === "pending_review" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Tu solicitud está en revisión, no hace falta que hagas nada
                  más.
                </p>
              )}

              {profile.status === "published" && !profile.ready && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Tu página sigue publicada, pero ya no cumple todos los
                  requisitos. Complétalos de nuevo para evitar problemas.
                </p>
              )}
            </div>

            {/* Compartir */}
            {profile.isLive && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3">
                  Compartir
                </h2>
                <a
                  href={profile.publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 mb-3 break-all"
                >
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                  {profile.publicUrl}
                </a>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleCopyLink}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    {copied ? "Copiado" : "Copiar enlace"}
                  </button>
                  <button
                    onClick={handleDownloadQr}
                    disabled={downloadingQr}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    {downloadingQr ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <QrCode className="w-4 h-4" />
                    )}
                    Descargar QR
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status banner
// ---------------------------------------------------------------------------

function StatusBanner({ profile }: { profile: PublicProfileData }) {
  switch (profile.status) {
    case "draft":
      return (
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 flex items-start gap-3">
          <Circle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-800">Borrador</p>
            <p className="text-sm text-gray-600 mt-0.5">
              Todavía no has solicitado la publicación. Completa los
              requisitos de la derecha y pulsa &ldquo;Publicar página&rdquo;.
            </p>
          </div>
        </div>
      );
    case "pending_review":
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-900">
              En revisión
            </p>
            <p className="text-sm text-yellow-800 mt-0.5">
              La estamos revisando
              {profile.submittedAt &&
                ` desde el ${formatDate(profile.submittedAt)}`}
              . Te avisaremos en cuanto esté publicada.
            </p>
          </div>
        </div>
      );
    case "published":
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-green-900">Publicada</p>
            <p className="text-sm text-green-800 mt-0.5">
              Tu página está en{" "}
              <a
                href={profile.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                {profile.publicUrl}
              </a>
              {profile.publishedAt &&
                ` desde el ${formatDate(profile.publishedAt)}`}
              . Los cambios que hagas se aplican en directo, sin volver a
              revisión. Tu página se guarda en caché para cargar rápido, así
              que puede tardar hasta un minuto en reflejarlos.
            </p>
          </div>
        </div>
      );
    case "rejected":
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">Rechazada</p>
            {profile.rejectionReason && (
              <p className="text-sm text-red-800 mt-0.5">
                <strong>Motivo:</strong> {profile.rejectionReason}
              </p>
            )}
            <p className="text-sm text-red-700 mt-1">
              Corrige lo necesario y vuelve a solicitar la publicación.
            </p>
          </div>
        </div>
      );
    default:
      return null;
  }
}
